import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { ScenarioNodeType } from './hooks/useLessonStateMachine'
import { playerMessages } from './CapeeshPlayer.i18n.Messages'

import WatchNode from './WatchNode'
import ListenNode from './ListenNode'
import SpeakNode from './SpeakNode'
import BranchingMultiChoiceNode from './BranchingMultiChoiceNode'
import RandomBranchNode from './RandomBranchNode'
import TestGenericNode from './TestGenericNode'
import TestDialogNode from './TestDialogNode'
import MultiChoiceNode from './MultiChoiceNode'
import VideoNode from './VideoNode'
import InstructionNode from './InstructionNode'
import TestDefinitionNode from './TestDefinitionNode'
import DebriefNode from './DebriefNode'

const LANGUAGE_ONLY_NODE_TYPES = new Set([
    ScenarioNodeType.Speak,
    ScenarioNodeType.TestDefinition,
    ScenarioNodeType.PronunciationSelfAssessment,
    ScenarioNodeType.WritingAiEvaluation,
])

/**
 * NodeRenderer — maps scenarioNodeType to the corresponding component.
 *
 * Props:
 *   node          – current ScenarioNodeDto
 *   imageMap      – { [url]: blobUrl }
 *   audioMap      – { [url]: blobUrl }
 *   onNodeComplete(nodeResult, interactiveResults?)  – called when node finishes
 *   onQuit        – called when user wants to quit the lesson
 *   scoring       – lesson scoring state (from useLessonScoring)
 *   lessonDto     – full lesson DTO (for debrief/report)
 *   isNativeSpeaker – if true, skip language-only nodes and filter translations
 */
const NodeRenderer = ({
    node,
    previousNode,
    imageMap,
    audioMap,
    onNodeComplete,
    onQuit,
    scoring,
    lessonDto,
    onRestart,
    isExamMode,
    isPhone,
    l2LanguageId,
    l1Id,
    customLessonId,
    isNativeSpeaker = false,
}) => {
    const intl = useIntl()

    useEffect(() => {
        if (isNativeSpeaker && node && LANGUAGE_ONLY_NODE_TYPES.has(node.scenarioNodeType)) {
            onNodeComplete({ nodeId: node.nodeId, timeUsed: 0, chosenAlternativeIndex: 0 })
        }
    }, [isNativeSpeaker, node, onNodeComplete])

    if (!node) return null

    if (isNativeSpeaker && LANGUAGE_ONLY_NODE_TYPES.has(node.scenarioNodeType)) {
        return null
    }

    const commonProps = {
        node,
        imageMap,
        audioMap,
        onNodeComplete,
        isExamMode,
        isPhone,
        l2LanguageId,
        l1Id,
        customLessonId,
        isNativeSpeaker,
    }

    const sceneProps = {
        ...commonProps,
        previousNode,
    }

    switch (node.scenarioNodeType) {
        case ScenarioNodeType.Watch:
            return <WatchNode {...sceneProps} />

        case ScenarioNodeType.Listen:
            return <ListenNode {...sceneProps} />

        case ScenarioNodeType.Instruction:
            return <InstructionNode {...commonProps} />

        case ScenarioNodeType.BranchingMultiChoice:
            return <BranchingMultiChoiceNode {...sceneProps} />

        case ScenarioNodeType.RandomBranch:
            return <RandomBranchNode {...sceneProps} />

        case ScenarioNodeType.Speak:
            return <SpeakNode {...sceneProps} lessonDto={lessonDto} />

        case ScenarioNodeType.TestDefinition:
            return <TestDefinitionNode {...commonProps} lessonDto={lessonDto} />

        case ScenarioNodeType.TestComprehension:
        case ScenarioNodeType.TestRule:
        case ScenarioNodeType.TestListenWithSlider:
        case ScenarioNodeType.TestListenFillInBlank:
        case ScenarioNodeType.TestImage:
        case ScenarioNodeType.TestAssociationPuzzle:
        case ScenarioNodeType.WritingAiEvaluation:
            return <TestGenericNode {...commonProps} />

        case ScenarioNodeType.TestDialog:
            return <TestDialogNode {...commonProps} />

        case ScenarioNodeType.MultiChoice:
        case ScenarioNodeType.ListenMultiChoice:
            return <MultiChoiceNode {...sceneProps} />

        case ScenarioNodeType.Video:
            return <VideoNode {...sceneProps} />

        case ScenarioNodeType.Debrief:
            return (
                <DebriefNode
                    {...commonProps}
                    scoring={scoring}
                    lessonDto={lessonDto}
                    onRestart={onRestart}
                    onQuit={onQuit}
                />
            )

        default:
            return (
                <div style={{ color: '#fff', textAlign: 'center', padding: 40 }}>
                    <p>{intl.formatMessage(playerMessages.unsupportedNodeType, { type: node.scenarioNodeType })}</p>
                    <button
                        onClick={() => onNodeComplete({ nodeId: node.nodeId, chosenAlternativeIndex: 0 })}
                        style={{
                            marginTop: 16,
                            padding: '12px 32px',
                            borderRadius: 24,
                            border: 'none',
                            background: '#fff',
                            color: '#333',
                            fontSize: 16,
                            fontWeight: 600,
                            cursor: 'pointer',
                        }}
                    >
                        {intl.formatMessage(playerMessages.skip)}
                    </button>
                </div>
            )
    }
}

export default NodeRenderer
